import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, Calendar, TrendingUp, ArrowRight, ExternalLink } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCanonical } from "../lib/seoHelpers";
import { safeJsonLd } from "../lib/safeJsonLd";

/**
 * /data/reports — public library of quarterly enforcement/appeal trend
 * reports. These are the "citable assets" journalists link to.
 *
 * Reports are compliance-safe: they document *what platforms enforce*, not
 * *how successful appeals are*. No recovery percentages, ever.
 */
const REPORTS = [
  {
    id: "appeal-outcomes-2026",
    quarter: "Live · Updated continuously",
    title: "Appeal Outcomes Report 2026 — Live Analyzer Data",
    summary:
      "Live research report pulling platform distribution, policy-category breakdown, and severity data from real submissions to the Free Suspension Analyzer. Honest sample-size caveats, no outcome percentages, free to cite.",
    highlights: [
      "Live data from the Free Suspension Analyzer",
      "Platform distribution + category breakdown + severity data",
      "No percentages published below n = 50 per bucket",
      "Full methodology disclosed on the page",
    ],
    published: "Oct 8, 2026 · Updated on every page load",
    downloadable: false,
    href: "/research/appeal-outcomes-2026",
  },
  {
    id: "q1-2026-enforcement-brief",
    quarter: "Q1 2026",
    title: "Social Media Enforcement Brief — Q1 2026",
    summary:
      "Analysis of platform enforcement patterns across Meta, TikTok, YouTube, and X. Covers policy category shifts, notice-language changes, and appeal-channel updates from the Oversight Board and platform Help Centers.",
    highlights: [
      "Meta added 3 new sub-categories under 'Coordinated Inauthentic Behavior'",
      "TikTok expanded 'Multiple Violations' window from 60 to 90 days",
      "YouTube consolidated Spam & Deceptive Practices into 4 policy areas",
      "X (Twitter) reintroduced verified-account appeal path",
    ],
    published: "February 20, 2026",
    downloadable: false, // set true when PDF is generated
  },
  {
    id: "2026-platform-policy-changelog",
    quarter: "Rolling",
    title: "2026 Platform Policy Changelog",
    summary:
      "Ongoing timeline of every published policy change across the 8 major social platforms we cover. Sourced from platform Newsrooms, Help Center updates, and Oversight Board decisions.",
    highlights: [
      "160+ policy touchpoints tracked",
      "Updated weekly from public sources",
      "Cross-referenced with actual case-notice language",
    ],
    published: "Ongoing · Updated weekly",
    downloadable: false,
    href: "/tools/tos-tracker",
  },
  {
    id: "appeal-channel-directory-2026",
    quarter: "Reference",
    title: "Official Appeal Channel Directory — 2026",
    summary:
      "Verified list of the *official* public appeal URLs for every major platform. If a service claims 'insider channels', you can cross-check against this list — every reputable appeal path is public.",
    highlights: [
      "10+ platforms covered",
      "Direct links to each platform's Help Center appeal form",
      "Includes legal-escalation Registered Agent addresses",
    ],
    published: "February 2026 · Updated as platforms revise their forms",
    downloadable: false,
    href: "/platform-registered-agent-addresses",
  },
];

export default function DataReportsPage() {
  useCanonical("https://socialdisputeco.com/data/reports");
  useEffect(() => {
    document.title = "Social Media Enforcement Reports & Data | Social Dispute Co";
    const desc = "Quarterly enforcement briefs, policy changelogs, and appeal-channel directories sourced from public platform documentation. Free for press, researchers, and journalists.";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);
  }, []);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Social Media Enforcement Reports",
    description:
      "Free, citable research reports on social media platform enforcement, published quarterly by Social Dispute Co.",
    publisher: { "@type": "Organization", name: "Social Dispute Co" },
    hasPart: REPORTS.map((r) => ({
      "@type": "Report",
      name: r.title,
      datePublished: r.published,
      url: `https://socialdisputeco.com/data/reports#${r.id}`,
    })),
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]" data-testid="data-reports-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(collectionSchema) }}
      />

      <Header />

      <main>
        <section className="pt-32 pb-12 bg-gradient-to-br from-[#3f4816]/40 to-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <p className="text-xs uppercase tracking-widest text-[#d9fb06] font-mono mb-3 flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5" />
              Free research · Citable · Compliance-safe
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-[#dfddd6] uppercase leading-[0.95] mb-5">
              Enforcement research & reports
            </h1>
            <p className="text-lg md:text-xl text-[#888680] max-w-2xl leading-relaxed">
              Quarterly enforcement briefs and reference directories sourced entirely from
              publicly-published platform documentation. Free to cite, quote, and share.
            </p>
            <p className="text-xs text-[#888680] mt-4">
              <strong>Note:</strong> We do not publish appeal-success percentages. Platform decisions are
              proprietary and any published &ldquo;recovery rate&rdquo; is either fabricated or unverifiable.
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="max-w-4xl mx-auto px-5 md:px-10 space-y-6">
            {REPORTS.map((report) => (
              <article
                key={report.id}
                id={report.id}
                className="rounded-2xl border border-[#3f4816]/50 bg-[#252825] p-6 md:p-8"
                data-testid={`report-${report.id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#d9fb06]/15 text-[#d9fb06] text-xs font-mono uppercase tracking-widest">
                    <Calendar className="w-3 h-3" />
                    {report.quarter}
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl font-black text-[#dfddd6] mb-3">
                  {report.title}
                </h2>
                <p className="text-sm md:text-base text-[#888680] mb-5 leading-relaxed">
                  {report.summary}
                </p>
                <ul className="space-y-1.5 mb-5">
                  {report.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-[#dfddd6]">
                      <FileText className="w-4 h-4 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-[#3f4816]/30">
                  <p className="text-xs text-[#888680] font-mono">Published: {report.published}</p>
                  {report.href ? (
                    <Link
                      to={report.href}
                      className="inline-flex items-center gap-1.5 text-[#d9fb06] hover:underline text-sm font-bold"
                    >
                      Read the report
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[#888680] text-sm">
                      <Download className="w-3.5 h-3.5" />
                      PDF available on request — email hello@socialdisputeco.com
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Press / cite this */}
        <section className="py-12 bg-[#302f2c] border-y border-[#3f4816]/40">
          <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
            <h2 className="text-xl md:text-2xl font-black text-[#d9fb06] uppercase mb-3">
              Citing our data?
            </h2>
            <p className="text-[#888680] mb-4 text-sm md:text-base">
              We&apos;re happy to be quoted, linked, or cited. Contact hello@socialdisputeco.com
              for an interview, quote, or original data pull for your story.
            </p>
            <a
              href="mailto:hello@socialdisputeco.com?subject=Press%20inquiry"
              className="inline-flex items-center gap-1.5 text-[#d9fb06] hover:underline text-sm font-bold"
            >
              hello@socialdisputeco.com
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
